import { useLocation } from 'react-router';

import { MyAccount } from '@/pages/MyAccount';
import { getVisitorOrigin } from '@/server-only/visitorOrigin';
import { getConfig } from '@/services/api';
import {
  getAllCountries,
  getFeaturedCountries,
} from '@/services/resources/ResourceService';
import { findMetaMatch, MetaStack } from '@/utils/metaUtils';
import { getCurrency } from '@/utils/ssrUtils';
import { getI18nInstance } from '~/middleware/i18next';

import { getSessionId } from '../../../src/server-only/sessions';
import { Route } from './+types/my-account';

export const loader = async ({ context, request }: Route.LoaderArgs) => {
  const visitorOrigin = await getVisitorOrigin(request);
  const sessionId = await getSessionId(request);
  const { t } = getI18nInstance(context);

  const config = await getConfig({ sessionId, visitorOrigin });

  const [countries, featuredCountries] = await Promise.all([
    getAllCountries(sessionId, visitorOrigin),
    getFeaturedCountries(
      sessionId,
      getCurrency({ request, config }),
      visitorOrigin,
    ),
  ]);

  const meta = {
    title: t('browser.myAccountTitle', 'My Account'),
  };

  return {
    meta,
    featuredCountries,
    countries,
  };
};

export const meta: Route.MetaFunction = ({ matches, data }: Route.MetaArgs) => {
  if (!data) return [];

  const rootMatch = findMetaMatch('root', matches);
  const metaStack = new MetaStack(rootMatch.meta);

  metaStack.title({
    title: data.meta.title,
    brandName: rootMatch.data.config.BRAND_NAME,
  });

  return metaStack.get();
};

export default function Component({ loaderData }: Route.ComponentProps) {
  const { featuredCountries, countries } = loaderData;
  const location = useLocation();
  const { returnPath } = location.state || {};

  return (
    <MyAccount
      key={location.key}
      featuredCountries={featuredCountries}
      countries={countries}
      returnPath={returnPath ?? undefined}
    />
  );
}
